import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { CountryLabel, countryName } from '../../../shared/components/CountryFlag';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Space, Tag, Descriptions, Alert, Form, Input, Select, message, Spin,
  Typography, Radio, Modal, Row, Col, Table, Dropdown, Switch, Collapse, Popconfirm, Empty, Tooltip, InputNumber
} from 'antd';
import {
  SyncOutlined, DownloadOutlined, CloudUploadOutlined, ReloadOutlined, StopOutlined,
  CheckCircleOutlined, ArrowLeftOutlined, CopyOutlined, ThunderboltOutlined, PlusOutlined
} from '@ant-design/icons';
import sslService from '../../services/sslService';
import { userPath } from '../../../shared/config/paths';
import { formatMoney } from '../../../shared/utils/money';
import AcmeSubscription from './AcmeSubscription';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS = {
  pending: ['待支付', 'default'],
  awaiting_configuration: ['待配置', 'blue'],
  pending_validation: ['待验证', 'orange'],
  validating: ['验证中', 'processing'],
  processing: ['签发中', 'processing'],
  issuing: ['签发中', 'processing'],
  active: ['已签发', 'success'],
  expired: ['已过期', 'default'],
  cancelled: ['已取消', 'default'],
  failed: ['失败', 'error'],
};
const METHOD_LABEL = { dns: 'DNS', http: 'HTTP 文件', email: '邮箱' };
const COUNTRIES = ['CN', 'HK', 'TW', 'SG', 'US', 'GB', 'DE', 'JP', 'KR', 'MY', 'AU', 'CA', 'FR', 'NL'];

const errText = (e, fb) => e?.error || e?.message || fb;

function Copy({ text }) {
  const { t } = useTranslation();
  if (!text) return null;
  return (
    <Space size={4} style={{ maxWidth: '100%' }}>
      <code style={{ wordBreak: 'break-all' }}>{text}</code>
      <CopyOutlined style={{ cursor: 'pointer', color: '#1677ff' }}
        onClick={() => { navigator.clipboard?.writeText(text); message.success(t('Copied')); }} />
    </Space>
  );
}

function ValidationTable({ rows, auto = [] }) {
  const { t } = useTranslation();
  if (!rows?.length) return <Text type="secondary">{t('Fetching verification details…')}</Text>;
  return (
    <Table size="small" pagination={false} rowKey={(r, i) => `${r.domain}-${i}`} dataSource={rows}
      scroll={{ x: true }}
      columns={[
        { title: t('Domains'), dataIndex: 'domain', render: (d) => (
          <Space>{d}{auto.includes(d) && <Tag color="green">{t('Automatic')}</Tag>}</Space>) },
        { title: t('Type'), dataIndex: 'type', width: 70 },
        { title: t('Name / Path'), render: (_, r) => <Copy text={r.method === 'http' ? (r.url || r.path) : (r.method === 'email' ? r.email : r.host)} /> },
        { title: t('Value'), render: (_, r) => r.method === 'email' ? <Text type="secondary">{t('Check your email and confirm')}</Text> : <Copy text={r.value} /> },
        { title: t('Status'), dataIndex: 'status', width: 80, render: (s) => s === 'valid'
          ? <Tag color="success">{t('Allow')}</Tag> : <Tag>{s === 'invalid' ? t('Failed') : t('Wait')}</Tag> },
      ]} />
  );
}

function ConfigureForm({ order, onDone }) {
  const { t } = useTranslation();
  const cap = order.capabilities || {};
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [emails, setEmails] = useState([]);
  const method = Form.useWatch('validation_method', form);
  const csrMode = Form.useWatch('csr_mode', form);

  const loadEmails = async () => {
    const d = form.getFieldValue('domain');
    if (!d || !cap.methods?.includes('email')) return;
    try {
      const r = await sslService.getApproverEmails(order.id, d);
      setEmails(r.data || []);
    } catch { setEmails([]); }
  };

  const submit = async (v) => {
    setBusy(true);
    try {
      const payload = {
        order_id: order.id,
        domain: v.domain,
        san_domains: v.san_domains || [],
        validation_method: v.validation_method,
        approver_email: v.approver_email,
        csr: v.csr_mode === 'paste' ? v.csr : undefined,
        key_type: v.key_type,
      };
      if (cap.requires_org) {
        payload.organization = v.org?.organization;
        payload.country = v.org?.country;
        payload.state = v.org?.state;
        payload.city = v.org?.city;
        payload.org_contact = v.org;
        payload.admin_contact = v.admin;
      }
      const r = await sslService.createOrder(payload);
      if (r.success) {
        message.success(r.data?.status === 'active' ? t('Certificate issued') : t('Submitted'));
        onDone();
      } else message.error(errText(r, t('Submission failed')));
    } catch (e) {
      message.error(errText(e, t('Submission failed')));
    } finally { setBusy(false); }
  };

  const methods = cap.methods || ['dns'];
  return (
    <Form form={form} layout="vertical" onFinish={submit} style={{ maxWidth: 720 }}
      initialValues={{
        domain: order.domain || undefined,
        san_domains: order.san_domains || [],
        validation_method: methods.includes(order.validation_method) ? order.validation_method : methods[0],
        csr_mode: 'auto', key_type: 'rsa',
        admin: order.admin_contact || {}, org: order.org_contact || {},
      }}>
      <Form.Item label={t("Domains")} name="domain" rules={[{ required: true, message: t('Please enter a domain') }]}
        extra={cap.wildcard ? t('Supports wildcards, e.g., *.example.com') : null}>
        <Input placeholder="example.com" onBlur={loadEmails} />
      </Form.Item>
      {cap.max_domains > 1 && (
        <Form.Item label={t('Additional domains (up to {{maxDomains}})', { maxDomains: cap.max_domains - 1 })} name="san_domains">
          <Select mode="tags" tokenSeparators={[',', ' ', '\n']} placeholder="www.example.com" open={false} />
        </Form.Item>
      )}
      <Form.Item label={t("Verification method")} name="validation_method"
        tooltip={t("Verification is automatic for domains hosted on Yeap DNS or connected to Yeap CDN")}>
        <Radio.Group optionType="button" buttonStyle="solid"
          options={methods.map((m) => ({ label: t(METHOD_LABEL[m] || m), value: m }))} />
      </Form.Item>
      {method === 'email' && (
        <Form.Item label={t("Verify email")} name="approver_email" rules={[{ required: true, message: t('Select a verification email address') }]}>
          <Select placeholder={t("Enter a domain first")} options={emails.map((e) => ({ label: e, value: e }))}
            onDropdownVisibleChange={(o) => o && !emails.length && loadEmails()} />
        </Form.Item>
      )}

      {cap.requires_org && (
        <>
          <Title level={5} style={{ marginTop: 8 }}>{t('Organization details')}</Title>
          <Row gutter={12}>
            <Col xs={24} md={12}><Form.Item label={t("Organization name")} name={['org', 'organization']} rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item label={t("Phone")} name={['org', 'phone']} rules={[{ required: true }]}><Input placeholder="+86 10 12345678" /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item label={t("Country")} name={['org', 'country']} rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="text" options={COUNTRIES.map((c) => ({ value: c, text: `${c} ${countryName(c)}`, label: <CountryLabel code={c}>{c}</CountryLabel> }))} /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item label={t("Province/state")} name={['org', 'state']} rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item label={t("City")} name={['org', 'city']} rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item label={t("Postal code")} name={['org', 'postal_code']}><Input /></Form.Item></Col>
            <Col span={24}><Form.Item label={t("Address")} name={['org', 'address']} rules={[{ required: true }]}><Input /></Form.Item></Col>
            {cap.ev && (<>
              <Col xs={24} md={8}><Form.Item label={t("Business type")} name={['org', 'business_category']} rules={[{ required: true }]}>
                <Select options={[
                  { label: t('Private company'), value: 'Private Organization' },
                  { label: t('Government agency'), value: 'Government Entity' },
                  { label: t('Business entity'), value: 'Business Entity' },
                  { label: t('Non-commercial entity'), value: 'Non-Commercial Entity' }]} /></Form.Item></Col>
              <Col xs={24} md={8}><Form.Item label={t("Registration number")} name={['org', 'registration_number']} rules={[{ required: true }]}><Input /></Form.Item></Col>
              <Col xs={24} md={8}><Form.Item label={t("Country of registration")} name={['org', 'jurisdiction_country']} rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="text" options={COUNTRIES.map((c) => ({ value: c, text: `${c} ${countryName(c)}`, label: <CountryLabel code={c}>{c}</CountryLabel> }))} /></Form.Item></Col>
            </>)}
          </Row>
          <Title level={5}>{t('Contacts')}</Title>
          <Row gutter={12}>
            <Col xs={12} md={6}><Form.Item label={t("First name")} name={['admin', 'first_name']} rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={12} md={6}><Form.Item label={t("Last name")} name={['admin', 'last_name']} rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={24} md={6}><Form.Item label={t("Email")} name={['admin', 'email']} rules={[{ required: true, type: 'email' }]}><Input /></Form.Item></Col>
            <Col xs={24} md={6}><Form.Item label={t("Phone")} name={['admin', 'phone']} rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
        </>
      )}

      <Collapse ghost style={{ marginBottom: 16, marginLeft: -16 }} items={[{
        key: 'adv', label: t('Advanced'),
        children: (
          <>
            <Form.Item label="CSR" name="csr_mode">
              <Radio.Group options={[{ label: t('Auto-generate (recommended)'), value: 'auto' }, { label: t('Use my CSR'), value: 'paste' }]} />
            </Form.Item>
            {csrMode === 'paste' ? (
              <Form.Item name="csr" rules={[{ required: true, message: t('Please paste the CSR') }]}>
                <TextArea rows={6} placeholder="-----BEGIN CERTIFICATE REQUEST-----" style={{ fontFamily: 'monospace' }} />
              </Form.Item>
            ) : (
              <Form.Item label={t("Key type")} name="key_type">
                <Radio.Group options={[{ label: 'RSA 2048', value: 'rsa' }, { label: 'ECDSA P-256', value: 'ec' }]} />
              </Form.Item>
            )}
          </>
        ),
      }]} />
      <Button type="primary" htmlType="submit" loading={busy}>{t('Submit')}</Button>
    </Form>
  );
}

function SanSlots({ order }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const s = order.capabilities?.san_slots;
  const [qty, setQty] = useState({ single: 0, wildcard: 0 });
  const [busy, setBusy] = useState(false);
  if (!s?.enabled) return null;
  const left = (t2) => (t2 === 'single' ? s.included : 0) + (s.bought?.[t2] || 0);
  const buy = async () => {
    setBusy(true);
    try {
      const r = await sslService.buySan(order.id, qty.single || 0, qty.wildcard || 0);
      if (r.success === false) throw r;
      const inv = r.data?.invoices?.[0];
      if (inv?.invoice_id) navigate(userPath(`/billing/invoices/${inv.invoice_id}`));
    } catch (e) { message.error(errText(e, t('Failed to place order'))); } finally { setBusy(false); }
  };
  return (
    <Collapse ghost size="small" style={{ marginLeft: -16, marginBottom: 8 }} items={[{
      key: 'san', label: t('Extra domain slots: {{s}} standard, {{w}} wildcard', { s: left('single'), w: left('wildcard') }),
      children: (
        <Space wrap>
          {['single', 'wildcard'].filter((k) => s.products?.[k]).map((k) => (
            <Space key={k}>
              <Text>{k === 'wildcard' ? t('Wildcard') : t('Standard')}</Text>
              <InputNumber min={0} max={100} value={qty[k]} onChange={(v) => setQty((q) => ({ ...q, [k]: v || 0 }))} />
              <Text type="secondary">{formatMoney(s.products[k].price, s.products[k].currency)}{t('/year')}</Text>
            </Space>
          ))}
          <Button size="small" loading={busy} disabled={!(qty.single > 0 || qty.wildcard > 0)} onClick={buy}>{t('Buy slots')}</Button>
        </Space>
      ),
    }]} />
  );
}

function ReissueModal({ order, open, onClose, onDone, addSan = false }) {
  const { t } = useTranslation();
  const cap = order.capabilities || {};
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const submit = async (v) => {
    setBusy(true);
    try {
      const r = await sslService.reissue(order.id, {
        san_domains: cap.max_domains > 1 ? (v.san_domains || []) : undefined,
        validation_method: v.validation_method,
        csr: v.csr || undefined,
        key_type: v.key_type,
      });
      if (r.success) { message.success(t('Reissue request submitted')); onDone(); onClose(); } else message.error(errText(r, t('Reissue failed')));
    } catch (e) { message.error(errText(e, t('Reissue failed'))); } finally { setBusy(false); }
  };
  return (
    <Modal title={addSan ? t('Add domain') : t("Reissue certificate")} open={open} onCancel={onClose} onOk={() => form.submit()} confirmLoading={busy} okText={t("Reissue")} destroyOnClose>
      {addSan && <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>{t('Reissued free with all domains included')}</Text>}
      <Form form={form} layout="vertical" onFinish={submit}
        initialValues={{ san_domains: order.san_domains || [], validation_method: order.validation_method, key_type: 'rsa' }}>
        {cap.max_domains > 1 && (
          <Form.Item label={t("Additional domains")} name="san_domains">
            <Select mode="tags" tokenSeparators={[',', ' ']} open={false} autoFocus={addSan} />
          </Form.Item>
        )}
        <SanSlots order={order} />
        <Form.Item label={t("Verification method")} name="validation_method">
          <Radio.Group optionType="button" options={(cap.methods || ['dns']).map((m) => ({ label: t(METHOD_LABEL[m]), value: m }))} />
        </Form.Item>
        <Form.Item label={t("Key type")} name="key_type">
          <Radio.Group options={[{ label: 'RSA 2048', value: 'rsa' }, { label: 'ECDSA P-256', value: 'ec' }]} />
        </Form.Item>
        <Form.Item label={t("CSR (optional)")} name="csr" tooltip={t("Leave blank to generate a new private key")}>
          <TextArea rows={4} placeholder="-----BEGIN CERTIFICATE REQUEST-----" style={{ fontFamily: 'monospace' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function DeployModal({ order, open, onClose, onDone }) {
  const { t } = useTranslation();
  const [targets, setTargets] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (!open) return;
    setTargets(null);
    sslService.getDeployTargets(order.id).then((r) => setTargets(r.data || [])).catch(() => setTargets([]));
  }, [open, order.id]);
  const go = async (target) => {
    setBusy(`${target.target}:${target.id}`);
    try {
      const r = await sslService.deploy(order.id, target.target, target.id);
      if (r.success) { message.success(r.message || t('Deployed')); onDone(); setTargets(null);
        const again = await sslService.getDeployTargets(order.id); setTargets(again.data || []); }
      else message.error(errText(r, t('Deployment failed')));
    } catch (e) { message.error(errText(e, t('Deployment failed'))); } finally { setBusy(null); }
  };
  return (
    <Modal title={t("Deploy certificate")} open={open} onCancel={onClose} footer={null} destroyOnClose>
      {targets === null ? <Spin /> : targets.length === 0 ? (
        <Empty description={t("No CDN domains or hosts match the domains on this certificate")} />
      ) : (
        <Table size="small" rowKey={(t) => `${t.target}:${t.id}`} pagination={false} dataSource={targets}
          columns={[
            { title: t('Domains'), dataIndex: 'name' },
            { title: t('Service'), dataIndex: 'label' },
            { title: '', align: 'right', render: (_, target) => (
              <Button size="small" type={target.deployed_at ? 'default' : 'primary'} loading={busy === `${target.target}:${target.id}`}
                onClick={() => go(target)}>{target.deployed_at ? t('Redeploy') : t('Deploy')}</Button>) },
          ]} />
      )}
    </Modal>
  );
}

export default function OrderDetail() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [hint, setHint] = useState('');
  const [reissueOpen, setReissueOpen] = useState(false);
  const [addSan, setAddSan] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await sslService.getOrder(orderId);
      if (r.success && r.data) setOrder(r.data);
      else { message.error(t('Order does not exist')); navigate(userPath('/ssl/certificates')); }
    } catch (e) {
      message.error(errText(e, t('Failed to load order')));
    } finally { setLoading(false); }
  }, [orderId, navigate]);

  useEffect(() => { load(); }, [load]);

  // poll while the CA is working
  useEffect(() => {
    if (!order) return undefined;
    const allAuto = (order.auto_validation || []).length > 0
      && (order.auto_validation || []).length >= (order.validation_data || []).length;
    const working = ['validating', 'processing', 'issuing'].includes(order.status)
      || (order.status === 'pending_validation' && allAuto)
      || (order.pending_action && !order.pending_action.error);
    if (!working) return undefined;
    const t = setTimeout(async () => {
      try { await sslService.refreshOrderStatus(order.id); } catch { /* ignore */ }
      load();
    }, 8000);
    return () => clearTimeout(t);
  }, [order, load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r.success === false) message.error(errText(r, t('Operation failed')));
      else if (ok) message.success(typeof ok === 'function' ? ok(r) : ok);
      return r;
    } catch (e) {
      message.error(errText(e, t('Operation failed')));
      return null;
    } finally { setBusy(''); load(); }
  };

  const verify = async () => {
    setHint('');
    const r = await run('verify', () => sslService.submitValidation(order.id));
    if (r?.success) {
      const st = r.data?.status;
      if (st === 'active') message.success(t('Certificate issued'));
      else if (r.data?.message) setHint(r.data.message);
    }
  };

  const saveBlob = (name, content, base64 = false, mime = 'application/octet-stream') => {
    const data = base64 ? Uint8Array.from(atob(content), (c) => c.charCodeAt(0)) : content;
    const url = URL.createObjectURL(new Blob([data], { type: mime }));
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const download = async (fmt) => {
    const base = (order.domain || 'certificate').replace('*', 'wildcard');
    if (['crt', 'key', 'ca', 'fullchain'].includes(fmt)) {
      const r = await sslService.downloadFormat(order.id, 'pem');
      if (!r?.success) return message.error(errText(r, t('Download failed')));
      const d = r.data;
      if (fmt === 'crt') saveBlob(`${base}.crt`, d.certificate);
      if (fmt === 'fullchain') saveBlob(`${base}.fullchain.pem`, d.fullchain);
      if (fmt === 'ca') saveBlob(`${base}.ca-bundle.crt`, d.ca_bundle || '');
      if (fmt === 'key') saveBlob(`${base}.key`, d.private_key || '');
      return undefined;
    }
    let password = '';
    if (fmt === 'pfx' || fmt === 'zip') {
      password = await new Promise((resolve) => {
        let val = '';
        Modal.confirm({
          title: fmt === 'pfx' ? t('PFX password') : t('PFX password (for IIS, optional)'),
          content: <Input.Password autoFocus onChange={(e) => { val = e.target.value; }} />,
          onOk: () => resolve(val), onCancel: () => resolve(null),
        });
      });
      if (password === null) return undefined;
    }
    const r = await run('dl', () => sslService.downloadFormat(order.id, fmt, password));
    if (r?.success) saveBlob(r.data.filename, r.data.content, true, r.data.mime);
    return undefined;
  };

  if (!order) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  const cap = order.capabilities || {};
  const [stText, stColor] = STATUS[order.status] || [order.status, 'default'];
  const revoked = !!order.revoked_at;
  const pa = order.pending_action;
  const isAcme = cap.acme;
  const isFailedUnconfigured = order.status === 'failed';
  const isCaas = !!cap.caas;
  const showConfigure = !isCaas && (order.status === 'awaiting_configuration' || isFailedUnconfigured);
  const names = [order.domain, ...(order.san_domains || [])].filter(Boolean);

  const downloadItems = [
    { key: 'zip', label: t('All (ZIP: Nginx / Apache / IIS)') },
    { key: 'fullchain', label: t('Certificate + chain (fullchain.pem)') },
    { key: 'crt', label: t('Certificate (.crt)') },
    { key: 'ca', label: t('Certificate chain (CA bundle)') },
    ...(order.has_private_key ? [{ key: 'key', label: t('Private key (.key)') }, { key: 'pfx', label: 'PFX（IIS / Windows）' }] : []),
    { key: 'der', label: 'DER' },
  ];

  return (
    <div style={{ maxWidth: 1000 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(userPath('/ssl/certificates'))}>{t('Certificate')}</Button>
        <Button icon={<SyncOutlined />} loading={busy === 'refresh' || loading}
          onClick={() => run('refresh', () => sslService.refreshOrderStatus(order.id))}>{t('Refresh')}</Button>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Space align="center" wrap style={{ marginBottom: 12 }}>
          <Title level={4} style={{ margin: 0 }}>{order.domain || order.product_name || t('Order #{{id}}', { id: order.id })}</Title>
          <Tag color={revoked ? 'red' : stColor}>{revoked ? t('Revoked') : t(stText)}</Tag>
          {order.is_staging && <Tag color="purple">{t('Test certificate')}</Tag>}
        </Space>
        <Descriptions size="small" column={{ xs: 1, md: 3 }}>
          <Descriptions.Item label={t("Product")}>{order.product_name || order.type}</Descriptions.Item>
          <Descriptions.Item label={t("Type")}>{cap.level || order.validation_type}</Descriptions.Item>
          <Descriptions.Item label={t("Order number")}>#{order.id}</Descriptions.Item>
          {names.length > 1 && <Descriptions.Item label={t("Domains")} span={3}>{names.join(', ')}</Descriptions.Item>}
          {order.expires_at && <Descriptions.Item label={t("Expires")}>{String(order.expires_at).slice(0, 10)}</Descriptions.Item>}
          {order.issuer && <Descriptions.Item label={t("Issuer")}>{order.issuer}</Descriptions.Item>}
          {order.status === 'active' && !isCaas && (
            <Descriptions.Item label={t("Auto-renewal")}>
              <Switch size="small" checked={!!order.auto_renew} loading={busy === 'ar'}
                onChange={(v) => run('ar', () => sslService.toggleAutoRenew(order.id, v), v ? t('Enabled') : t('Closed'))} />
              {!isAcme && order.auto_renew && <Text type="secondary" style={{ marginLeft: 8 }}>{t('Generate a renewal invoice before expiration')}</Text>}
            </Descriptions.Item>
          )}
        </Descriptions>
        {order.error_message && !['active'].includes(order.status) && (
          <Alert type="error" showIcon style={{ marginTop: 12 }} message={order.error_message} />
        )}
      </Card>

      {order.status === 'pending' && (
        <Card>
          <Alert type="warning" showIcon message={order.payment_error || t('Order awaiting payment')}
            action={order.invoice?.id && (
              <Button type="primary" onClick={() => navigate(userPath(`/billing/invoices/${order.invoice.id}`))}>{t('Pay now')}</Button>
            )} />
        </Card>
      )}

      {showConfigure && (
        <Card title={isFailedUnconfigured ? t('Resubmit') : t('Configure certificate')}>
          {cap.staging && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t("Test mode: issued by Let's Encrypt Staging (not trusted by browsers)")} />}
          <ConfigureForm order={order} onDone={load} />
        </Card>
      )}

      {isCaas && <AcmeSubscription order={order} onChanged={load} />}

      {!isCaas && ['pending_validation', 'validating'].includes(order.status) && (
        <Card title={t("Domain verification")}
          extra={<Button type="primary" icon={<CheckCircleOutlined />} loading={busy === 'verify'} onClick={verify}>{t('Verify')}</Button>}>
          {order.status === 'validating' && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t("CA validation in progress…")} />}
          {order.auto_validation?.length > 0 && order.auto_validation.length === (order.validation_data || []).length
            ? <Alert type="success" showIcon icon={<ThunderboltOutlined />} style={{ marginBottom: 12 }} message={t("Verification record added automatically. No action required")} />
            : null}
          <ValidationTable rows={order.validation_data} auto={order.auto_validation} />
          {hint && <Alert type="warning" showIcon style={{ marginTop: 12 }} message={hint} />}
          {order.validation_method === 'email' && !isAcme && (
            <Button style={{ marginTop: 12 }} loading={busy === 'resend'}
              onClick={() => run('resend', () => sslService.resendNotifications(order.id), t('Email resent'))}>{t('Resend verification email')}</Button>
          )}
        </Card>
      )}

      {!isCaas && ['processing', 'issuing'].includes(order.status) && (
        <Card><Alert type="info" showIcon icon={<Spin size="small" />} message={t("Issuing certificate")}
          description={cap.level === 'DV' ? null : t('OV/EV certificates require manual review of organization details by the CA, usually taking 1–5 business days.')} /></Card>
      )}

      {order.status === 'active' && !isCaas && (
        <Card>
          {pa && (
            <Alert type={pa.error ? 'error' : 'info'} showIcon style={{ marginBottom: 16 }}
              message={pa.error || t('{{action}} in progress; the current certificate remains valid', { action: pa.type === 'renew' ? t('Renew') : t('Reissue') })}
              description={!pa.error && <>
                <ValidationTable rows={pa.validation_data} auto={Object.keys(pa.auto || {})} />
                <Button size="small" type="primary" style={{ marginTop: 8 }} loading={busy === 'verify'} onClick={verify}>{t('Verify')}</Button>
                {hint && <div style={{ marginTop: 8 }}><Text type="warning">{hint}</Text></div>}
              </>} />
          )}
          <Space wrap>
            <Dropdown menu={{ items: downloadItems, onClick: ({ key }) => download(key) }}>
              <Button type="primary" icon={<DownloadOutlined />} loading={busy === 'dl'}>{t('Download')}</Button>
            </Dropdown>
            <Tooltip title={order.is_staging ? t('Test certificates cannot be deployed') : ''}>
              <Button icon={<CloudUploadOutlined />} disabled={order.is_staging || !order.has_private_key}
                onClick={() => setDeployOpen(true)}>{t('Deploy to CDN / host')}</Button>
            </Tooltip>
            {order.provider !== 'manual' && !cap.code_signing && cap.max_domains > 1 && !isAcme && (
              <Button icon={<PlusOutlined />} disabled={!!(pa && !pa.error)} onClick={() => { setAddSan(true); setReissueOpen(true); }}>{t('Add domain')}</Button>
            )}
            {order.provider !== 'manual' && !cap.code_signing && (
              <Button icon={<ReloadOutlined />} disabled={!!(pa && !pa.error)} onClick={() => { setAddSan(false); setReissueOpen(true); }}>{t('Reissue')}</Button>
            )}
            {order.provider !== 'manual' && (
              <Button loading={busy === 'renew'} disabled={!!(pa && !pa.error)}
                onClick={() => run('renew', () => sslService.renewCertificate(order.id), (r) => r.data?.requires_payment ? t('Renewal invoice generated') : t('Renewal started'))
                  .then((r) => { if (r?.data?.invoice_id) navigate(userPath(`/billing/invoices/${r.data.invoice_id}`)); })}>
                {isAcme ? t('Renew now') : t('Renew')}
              </Button>
            )}
            {order.provider !== 'manual' && (
              <Popconfirm title={t("Revoking this certificate invalidates it immediately and cannot be undone")} okText={t("Revoke")} okButtonProps={{ danger: true }}
                onConfirm={() => run('revoke', () => sslService.revoke(order.id, 'unspecified'), t('Revoked'))}>
                <Button danger icon={<StopOutlined />} loading={busy === 'revoke'}>{t('Revoke')}</Button>
              </Popconfirm>
            )}
          </Space>
          {order.deployments?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">{t('Deployed:')}</Text>{order.deployments.map((d) => <Tag key={`${d.target}${d.id}`}>{d.name}</Tag>)}
            </div>
          )}
          {!order.has_private_key && <div style={{ marginTop: 8 }}><Text type="secondary">{t('Issue using your own CSR; the private key stays on your device.')}</Text></div>}
        </Card>
      )}

      {order.status === 'expired' && order.provider !== 'manual' && !isCaas && (
        <Card>
          <Space>
            <Text>{t('Certificate expired')}</Text>
            <Button type="primary" loading={busy === 'renew'}
              onClick={() => run('renew', () => sslService.renewCertificate(order.id), t('Renewal started'))}>{t('Renew')}</Button>
          </Space>
        </Card>
      )}

      {!isCaas && <ReissueModal order={order} open={reissueOpen} addSan={addSan} onClose={() => setReissueOpen(false)} onDone={load} />}
      <DeployModal order={order} open={deployOpen} onClose={() => setDeployOpen(false)} onDone={load} />
    </div>
  );
}
